import { VideoEditor } from '../VideoEditor';
import { Element } from '../models/Element';
import { AbstractElementCommand } from './AbstractCommand';
import { VideoElement } from '../models/elements/VideoElement';
import { AudioElement } from '../models/elements/AudioElement';
import { EditorChangeType } from '../types';

export class SplitElementCommand extends AbstractElementCommand {
  private splitTime: number;
  private firstResultElement: Element | null = null;
  private secondResultElement: Element | null = null;
  private trackId: string | null | undefined = null;

  constructor(editor: VideoEditor, element: Element, splitTime: number) {
    super(editor, element);
    this.splitTime = this.snapTimeToFrame(splitTime);
  }

  execute(): boolean {
    // Store the track for undo
    this.trackId = this.editor.timeline.getTrackIdForElement(this.sourceElement.id);
    if (!this.trackId) return false;

    // Use TrackManager to perform the split
    const trackManager = this.editor.getTrackManager();
    const result = trackManager.splitElement(this.sourceElement, this.splitTime);
    if (!result) return false;

    // Store the resulting elements for undo and notification
    [this.firstResultElement, this.secondResultElement] = result;

    // Recalculate timeline duration
    this.recalculateTimeline();

    // Notify observers about the split
    this.editor.notifyObservers(EditorChangeType.ELEMENT_SPLIT, {
      originalElement: this.sourceElement,
      firstElement: this.firstResultElement,
      secondElement: this.secondResultElement,
      splitTime: this.splitTime
    });

    return true;
  }

  undo(): boolean {
    if (!this.firstResultElement || !this.secondResultElement || !this.trackId) {
      return false;
    }

    // Remove both split elements
    this.editor.timeline.removeElement(this.firstResultElement.id);
    this.editor.timeline.removeElement(this.secondResultElement.id);

    // Restore the original element
    this.editor.timeline.addElementToTrack(this.trackId, this.sourceElement);

    // Recalculate timeline duration
    this.recalculateTimeline();

    // Notify observers about the undo
    this.editor.notifyObservers(EditorChangeType.ELEMENT_SPLIT_UNDONE, {
      originalElement: this.sourceElement,
      firstElement: this.firstResultElement,
      secondElement: this.secondResultElement,
      splitTime: this.splitTime
    });

    return true;
  }

  getDescription(): string {
    return `Split element ${this.sourceElement.id} at ${this.splitTime}s`;
  }

  // Expose the result elements for commandSplitElement to return
  getResultElements(): [Element, Element] | null {
    if (!this.firstResultElement || !this.secondResultElement) {
      return null;
    }
    return [this.firstResultElement, this.secondResultElement];
  }
}